import { Link } from "react-router-dom";
import { ArrowRight, Bitcoin, LineChart, TrendingUp, CandlestickChart, Eye, Network, Lock } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const HERO_BG_GRADIENT =
  "radial-gradient(ellipse at 50% 30%, rgba(70,70,70,0.35) 0%, rgba(10,10,10,0.95) 55%, #000 100%)";

const TEAM = [
  {
    name: "Issy Flash",
    role: "Crypto Mentor",
    tag: "ON-CHAIN INSIDER",
    icon: Bitcoin,
    img: "https://customer-assets-jai6qajn.emergentagent.net/job_community-redesign-1/artifacts/8dhx27kj_4AE06A78-6045-4A71-AEDA-0A76307E4114.PNG",
    bio: "Turned $100 into $10,000 on SafeMoon at 18 and never looked back. Now runs on-chain plays for The Syndicate with a network of founders, whales and market-makers most traders only read about on Twitter.",
  },
  {
    name: "CB Capital",
    role: "Forex Mentor",
    tag: "INSTITUTIONAL INSIDER",
    icon: LineChart,
    img: "https://customer-assets-jai6qajn.emergentagent.net/job_community-redesign-1/artifacts/mwhbaqno_IMG_5C5DA6C4E1D9-1.jpeg",
    bio: "Trades majors, minors and metals with an institutional lens — reads liquidity, structure and macro flow the way desks do, giving members setups the retail crowd never sees coming.",
  },
  {
    name: "Razor",
    role: "Forex Mentor",
    tag: "PRICE-ACTION INSIDER",
    icon: TrendingUp,
    img: "https://customer-assets-jai6qajn.emergentagent.net/job_community-redesign-1/artifacts/8r7p7ipw_IMG_5421.webp",
    bio: "Pure price action, no noise. Razor reads the tape like a language — order flow, killzones and session opens — teaching members the discipline that separates insiders from retail.",
  },
  {
    name: "Zac",
    role: "Stocks Mentor",
    tag: "EQUITIES INSIDER",
    icon: CandlestickChart,
    img: "/zac_generated_0.png",
    bio: "An anonymous equities operator running high-conviction plays on US large-caps, options flow and momentum names. Stays off-camera on purpose — the trades and the P&L do the talking.",
  },
];

const INSIDER_POINTS = [
  {
    icon: Eye,
    title: "We See What Retail Doesn't",
    desc: "Order flow, on-chain wallets, dark pool prints — the signals professional desks watch, translated for members in real time.",
  },
  {
    icon: Network,
    title: "Rooms You're Not Invited To",
    desc: "Founder chats, market-maker groups, prop-desk backchannels. Our mentors sit inside them and bring the alpha back to The Syndicate.",
  },
  {
    icon: Lock,
    title: "Not For Sale Publicly",
    desc: "The plays, contacts and setups shared inside these rooms never touch social media. If you're not in, you don't see them.",
  },
];

export default function Team() {
  return (
    <div className="relative min-h-screen bg-black text-white overflow-x-hidden grain" data-testid="team-page">
      <Navbar />

      {/* HERO */}
      <section
        data-testid="team-hero"
        className="relative min-h-[52vh] flex flex-col items-center justify-center px-6 pt-36 pb-14"
      >
        <div
          className="absolute inset-0 opacity-95"
          style={{ background: HERO_BG_GRADIENT }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/75 to-black" />
        <div className="absolute inset-0 dot-pattern opacity-40" />

        <div className="relative z-10 text-center max-w-3xl">
          <p className="font-mono-serif text-[10px] md:text-[11px] tracking-[0.45em] text-white/45 fade-up delay-1">
            THE INSIDERS  //  BEHIND THE SYNDICATE
          </p>
          <h1 className="mt-5 font-display text-4xl md:text-6xl lg:text-7xl tracking-tight fade-up delay-2">
            Meet the Team
          </h1>
          <p className="mt-6 text-white/60 max-w-2xl mx-auto text-sm md:text-base leading-relaxed fade-up delay-3">
            Every mentor here is a market <span className="text-white">insider</span> — not a course seller.
            They sit inside the rooms retail never sees: on-chain founder chats, prop-desk backchannels
            and institutional flow. When they trade, you trade with them.
          </p>
        </div>
      </section>

      {/* INSIDER CIRCLE STRIP */}
      <section className="relative z-10 px-4 md:px-6 -mt-4">
        <div className="max-w-6xl mx-auto">
          <p className="text-center font-mono-serif text-[10px] tracking-[0.4em] text-white/45">
            THE INSIDER CIRCLE  //  WHAT THAT MEANS
          </p>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {INSIDER_POINTS.map((pt) => (
              <div
                key={pt.title}
                data-testid={`insider-point-${pt.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="frame p-6 md:p-7 flex flex-col items-start"
              >
                <div className="h-10 w-10 rounded-full border border-white/10 bg-white/5 flex items-center justify-center">
                  <pt.icon size={16} className="text-white/85" />
                </div>
                <h3 className="mt-4 font-mono-serif text-[11px] tracking-[0.28em] text-white">
                  {pt.title.toUpperCase()}
                </h3>
                <p className="mt-3 text-white/60 text-sm leading-relaxed">
                  {pt.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM GRID */}
      <section className="relative z-10 px-4 md:px-6 mt-16 md:mt-24 pb-24">
        <div className="max-w-6xl mx-auto">
          <p className="text-center font-mono-serif text-[10px] tracking-[0.4em] text-white/45 mb-8">
            FOUR SEATS  //  ONE INSIDER CIRCLE
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {TEAM.map((m, i) => (
            <article
              key={m.name}
              data-testid={`team-card-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="group frame overflow-hidden flex flex-col hover:border-white/20 transition duration-500"
            >
              {/* Portrait */}
              <div className="relative aspect-[4/5] w-full overflow-hidden bg-neutral-950">
                <img
                  src={m.img}
                  alt={m.name}
                  className="absolute inset-0 h-full w-full object-cover transition duration-700 ease-out group-hover:scale-[1.04]"
                  loading="lazy"
                />
                {/* gradient wash */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                {/* tag badge */}
                <div className="absolute top-4 left-4">
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/55 backdrop-blur border border-white/10">
                    <m.icon size={12} className="text-white/85" />
                    <span className="font-mono-serif text-[9px] tracking-[0.3em] text-white/85">
                      {m.tag}
                    </span>
                  </span>
                </div>
                {/* name overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-7">
                  <p className="font-mono-serif text-[10px] tracking-[0.35em] text-white/60">
                    {`0${i + 1}  //  ${m.role.toUpperCase()}`}
                  </p>
                  <h3 className="mt-2 font-display text-3xl md:text-4xl tracking-tight">
                    {m.name}
                  </h3>
                </div>
              </div>

              {/* Bio */}
              <div className="px-6 md:px-8 py-6 md:py-7 border-t border-white/5">
                <p className="text-white/65 text-sm md:text-[15px] leading-relaxed">
                  {m.bio}
                </p>
              </div>
            </article>
          ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative z-10 px-4 md:px-6 pb-24">
        <div className="max-w-3xl mx-auto text-center frame p-10 md:p-14">
          <h2 className="font-display text-3xl md:text-5xl">Trade alongside the insiders</h2>
          <p className="mt-4 text-white/60 max-w-xl mx-auto">
            Membership opens the door to the insider circle — a private line to every mentor above, plus their live ideas, execution and market flow every single day.
          </p>
          <Link
            to="/pricing"
            data-testid="team-join-btn"
            className="mt-8 inline-flex items-center gap-3 btn-shimmer text-black font-mono-serif text-xs tracking-[0.3em] px-8 py-4 rounded-full hover:opacity-95 transition"
          >
            APPLY FOR ENTRY <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
